import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { createOidcAuthProvider } from './oidcAuthProvider';
import { AuthProvider } from 'ra-core';

// Get OIDC configuration from environment variables
const authority = import.meta.env.VITE_OIDC_AUTHORITY || import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID || import.meta.env.VITE_AUTH0_CLIENT_ID;
const redirectUri = import.meta.env.VITE_LOGIN_REDIRECT_URL;
const postLogoutRedirectUri = import.meta.env.VITE_LOGOUT_REDIRECT_URL;
// offline_access is required for refresh tokens to work with automaticSilentRenew
const scope = import.meta.env.VITE_OIDC_SCOPE || 'openid profile email offline_access';

/**
 * Ensure authority URL has proper protocol
 *
 * - Returns empty string if auth is empty
 * - Returns as-is if protocol already specified
 * - Uses HTTP for local development URLs (localhost, 127.0.0.1, 0.0.0.0)
 * - Uses HTTPS for all other domains
 *
 * @param auth - Authority domain or URL
 * @returns Authority URL with protocol
 */
const getAuthorityWithProtocol = (auth: string): string => {
  if (!auth) return '';
  if (auth.startsWith('http://') || auth.startsWith('https://')) {
    return auth;
  }

  // Check if it's a local development URL
  const isLocal = auth.includes('localhost') ||
                  auth.startsWith('127.0.0.1') ||
                  auth.startsWith('0.0.0.0') ||
                  auth.endsWith('.local'); // Support custom dev domains like dev.local

  return isLocal ? `http://${auth}` : `https://${auth}`;
};

const authorityUrl = getAuthorityWithProtocol(authority);

/**
 * UserManager instance for OIDC authentication
 * Works with any OAuth2/OIDC compliant provider (including custom backends)
 */
export const userManager = new UserManager({
  authority: authorityUrl,
  client_id: clientId!,
  redirect_uri: redirectUri || `${window.location.origin}/auth-callback`,
  post_logout_redirect_uri: postLogoutRedirectUri || window.location.origin,
  response_type: 'code',
  scope: scope,

  // Automatic OIDC Discovery
  // The library will fetch configuration from: {authority}/.well-known/openid-configuration
  // This provides all endpoint URLs automatically (authorize, token, userinfo, jwks, etc.)
  // No need to manually specify metadata - it will be discovered dynamically

  // Use localStorage for token storage
  userStore: new WebStorageStateStore({ store: window.localStorage }),

  // PKCE (Proof Key for Code Exchange) is enabled by default in oidc-client-ts
  // when using response_type: 'code' (authorization code flow)
  // The library automatically uses S256 (SHA-256) challenge method for enhanced security
  // This protects against authorization code interception attacks

  // Automatic token renewal
  automaticSilentRenew: true,
  loadUserInfo: true,

  // Session monitoring
  monitorSession: false,
});

/**
 * Auth provider for React Admin
 */
export const authProvider: AuthProvider = createOidcAuthProvider(userManager, {
  loginRedirectUri: redirectUri,
  logoutRedirectUri: postLogoutRedirectUri,
  redirectOnCheckAuth: true,
});

/**
 * Performs local logout by clearing OIDC-related storage keys
 * This is a fallback when server-side logout is unavailable
 *
 * SECURITY NOTE:
 * - Relies primarily on UserManager.removeUser() for cleanup
 * - Only clears keys with OIDC-specific prefixes to avoid affecting other app data
 * - Pattern: oidc-client-ts uses keys like "oidc.user:{authority}:{client_id}"
 *
 * @param userManager - The OIDC UserManager instance
 */
export const performLocalLogout = async (userManager: UserManager): Promise<void> => {
  console.warn('[OIDC] Server-side logout unavailable, performing client-only logout');

  // Remove OIDC user data - this is the primary cleanup method
  // It removes keys like: oidc.user:{authority}:{client_id}
  await userManager.removeUser();

  // Clear only OIDC-specific keys as a safety measure
  // Only target keys that start with 'oidc.' to avoid clearing unrelated app data
  const oidcKeys = Object.keys(localStorage).filter(k => k.startsWith('oidc.'));
  oidcKeys.forEach(k => localStorage.removeItem(k));

  // Clear OIDC keys from session storage
  const sessionOidcKeys = Object.keys(sessionStorage).filter(k => k.startsWith('oidc.'));
  sessionOidcKeys.forEach(k => sessionStorage.removeItem(k));

  // Clear React Admin's previous location storage to prevent redirect loops
  localStorage.removeItem('RaStore.previousLocation');
};

/**
 * Helper function to get access token for API calls
 */
export const getAccessToken = async (): Promise<string | null> => {
  const user = await userManager.getUser();
  return user?.access_token || null;
};

/**
 * Helper function to check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const user = await userManager.getUser();
  return !!(user && !user.expired);
};

/**
 * Helper function to get user profile
 */
export const getUserProfile = async () => {
  const user = await userManager.getUser();
  return user?.profile || null;
};

/**
 * Export userManager as oidc for compatibility with existing code
 * This allows code like `oidc.isAuthenticated()` to work
 */
export const oidc = {
  isAuthenticated,
  getUser: () => userManager.getUser(),
  logout: async (options?: { returnTo?: string }) => {
    try {
      await userManager.signoutRedirect({
        post_logout_redirect_uri: options?.returnTo || postLogoutRedirectUri || window.location.origin,
        // The auth server needs the client to validate the post-logout redirect, to keep the
        // product's branding on whatever page the user lands on, and to know where to send
        // them if they log in again.
        //
        // Supplied only when the stored user HAS an id_token. oidc-client-ts sets
        // id_token_hint from the stored user and then adds client_id itself precisely when
        // there is none (`!client_id && post_logout_redirect_uri && !id_token_hint`), and
        // SignoutRequest appends rather than replaces — so sending it unconditionally emitted
        // `client_id` twice for magic-link and invitation sessions, which hold no id_token.
        // A strict IdP rejects a repeated parameter as invalid_request (RFC 6749 §3.1).
        // Either way exactly one client_id reaches the end-session endpoint.
        ...((await userManager.getUser())?.id_token
            ? { extraQueryParams: { client_id: userManager.settings.client_id } }
            : {}),
      });
    } catch (error) {
      // Check if error is specifically about missing end_session_endpoint
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isMissingEndpoint = errorMessage.toLowerCase().includes('end_session_endpoint') ||
                                 errorMessage.toLowerCase().includes('no end session endpoint');

      if (isMissingEndpoint) {
        // Fallback: Construct manual logout URL with OAuth2 standard parameters
        console.warn('[OIDC] Provider does not advertise end_session_endpoint, using manual logout URL');
        const redirectUri = options?.returnTo || postLogoutRedirectUri || window.location.origin;
        const logoutUrl = `${authorityUrl}/oauth2/logout?` +
          `post_logout_redirect_uri=${encodeURIComponent(redirectUri)}` +
          `&client_id=${encodeURIComponent(clientId!)}`;

        await performLocalLogout(userManager);
        window.location.href = logoutUrl;
      } else {
        // Unexpected error (network, etc.) - re-throw to surface the issue
        console.error('[OIDC] Unexpected logout error:', error);
        throw error;
      }
    }
  },
  loginWithRedirect: (options?: { authorizationParams?: { redirect_uri?: string; login_hint?: string } }) =>
    userManager.signinRedirect({
      redirect_uri: options?.authorizationParams?.redirect_uri || redirectUri || `${window.location.origin}/auth-callback`,
      extraQueryParams: options?.authorizationParams?.login_hint
        ? { login_hint: options.authorizationParams.login_hint }
        : undefined,
    }),
  handleRedirectCallback: () => userManager.signinRedirectCallback(),
};
